import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import { Checkbox, Label } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

export enum RuleStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
}

interface MultiSelectRuleStatusProps {
  selectedRuleStatus: RuleStatus[];
  size?: 'sm' | 'md';
  isOpen?: boolean;
  icon?: React.ReactNode;
  onChange: (status: RuleStatus[]) => void;
}

const MultiSelectRuleStatus = ({ size = 'md', isOpen, icon, selectedRuleStatus, onChange }: MultiSelectRuleStatusProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedRuleStatus || []);

  // check if all selected types are in draft
  const hasSameElements = selectedRuleStatus?.length === draft?.length && selectedRuleStatus.every((value) => draft.find((d) => d === value));

  const onClick = useCallback(() => {
    setDraft(selectedRuleStatus || []);
  }, [selectedRuleStatus]);

  const onChangeSelect = (status: RuleStatus) => {
    const isSelected = draft.find((d) => d === status);
    const updatedSelected = isSelected ? draft.filter((d) => d !== status) : [...draft, status];
    setDraft(updatedSelected);
  };

  const handleClear = useCallback(() => {
    setDraft([]);
  }, []);

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const data = useMemo(
    () => [
      { value: RuleStatus.ENABLED, label: t('rules.enabled_rules') },
      { value: RuleStatus.DISABLED, label: t('rules.disabled_rules') },
    ],
    [t]
  );

  return (
    <Dropdown
      portal={false}
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('rules.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={200}
              heightItem={28}
              data={data}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item.value} key={item.value} checked={draft.includes(item.value)} onCheckedChange={() => onChangeSelect(item.value)} />
                  <Label htmlFor={item.value} className='ellipsis w-full overflow-hidden' title={item.label}>
                    {item.label}
                  </Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button variant='ghost' full size='sm' onClick={handleClose}>
                {t('rules.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('rules.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={icon ?? <Icon name='bell' />}>
        {t('rules.title')} {(selectedRuleStatus?.length || 0) > 0 ? `(${selectedRuleStatus?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectRuleStatus;
